/**
 * Validation utilities for form inputs
 */

/**
 * Validates ISBN-10 format
 * @param isbn The ISBN string to validate (digits only, no hyphens)
 * @returns true if valid ISBN-10
 */
export function isValidISBN10(isbn: string): boolean {
  // ISBN-10 must be exactly 10 characters
  if (isbn.length !== 10) return false;

  // All characters must be digits except last one can be 'X'
  if (!/^\d{9}[\dX]$/.test(isbn)) return false;

  // Calculate checksum
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(isbn[i]) * (10 - i);
  }

  const checkDigit = isbn[9] === 'X' ? 10 : parseInt(isbn[9]);
  sum += checkDigit;

  return sum % 11 === 0;
}

/**
 * Validates ISBN-13 format
 * @param isbn The ISBN string to validate (digits only, no hyphens)
 * @returns true if valid ISBN-13
 */
export function isValidISBN13(isbn: string): boolean {
  // ISBN-13 must be exactly 13 digits
  if (isbn.length !== 13) return false;

  // All characters must be digits
  if (!/^\d{13}$/.test(isbn)) return false;

  // Calculate checksum
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const multiplier = i % 2 === 0 ? 1 : 3;
    sum += parseInt(isbn[i]) * multiplier;
  }

  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit === parseInt(isbn[12]);
}

/**
 * Validates ISBN (both ISBN-10 and ISBN-13)
 * Accepts ISBN with or without hyphens/spaces
 * NOTE: Checksum validation disabled - accepts any alphanumeric ISBN value
 * @param isbn The ISBN string to validate
 * @returns Object with validation result and formatted ISBN
 */
export function validateISBN(isbn: string): {
  isValid: boolean;
  formattedISBN: string;
  type: 'ISBN-10' | 'ISBN-13' | null;
  error?: string;
} {
  if (!isbn || typeof isbn !== 'string') {
    return {
      isValid: false,
      formattedISBN: '',
      type: null,
      error: 'ISBN is required',
    };
  }

  // Remove all hyphens, spaces, and convert to uppercase
  const cleanISBN = isbn.replace(/[\s-]/g, '').toUpperCase();

  // Check if empty after cleaning
  if (cleanISBN.length === 0) {
    return {
      isValid: false,
      formattedISBN: '',
      type: null,
      error: 'ISBN cannot be empty',
    };
  }

  // Accept any non-empty value without checksum validation
  // Determine type based on length (for informational purposes only)
  let type: 'ISBN-10' | 'ISBN-13' | null = null;
  if (cleanISBN.length === 10) {
    type = 'ISBN-10';
  } else if (cleanISBN.length === 13) {
    type = 'ISBN-13';
  }

  return {
    isValid: true, // Accept any value
    formattedISBN: cleanISBN,
    type,
    error: undefined,
  };
}

/**
 * Formats ISBN with hyphens for display
 * @param isbn The ISBN string to format
 * @returns Formatted ISBN string
 */
export function formatISBN(isbn: string): string {
  const cleanISBN = isbn.replace(/[\s-]/g, '').toUpperCase();

  if (cleanISBN.length === 10) {
    // Format ISBN-10: X-XXX-XXXXX-X
    return `${cleanISBN.slice(0, 1)}-${cleanISBN.slice(1, 4)}-${cleanISBN.slice(4, 9)}-${cleanISBN.slice(9)}`;
  } else if (cleanISBN.length === 13) {
    // Format ISBN-13: XXX-X-XXX-XXXXX-X
    return `${cleanISBN.slice(0, 3)}-${cleanISBN.slice(3, 4)}-${cleanISBN.slice(4, 7)}-${cleanISBN.slice(7, 12)}-${cleanISBN.slice(12)}`;
  }

  return cleanISBN;
}
